/**
 * Écran « Construction budgétaire ».
 *
 * Une grille exercices × postes, éditable poste par poste. Un montant laissé
 * tel quel est engendré par son inducteur ; un montant saisi est verrouillé et
 * marqué. C'est la distinction qui rend le budget lisible : on voit d'un coup
 * d'œil ce qui est arbitré et ce qui suit une règle.
 */

import { entier, euros, eurosSigne, nombre, pourcent } from '@scc/core';
import type { Charge } from '../api.js';
import { arbitrerLigne, reinitialiserLigne } from '../api.js';
import { h, vider } from '../dom.js';
import { badge, barreExport, enteteSection, insight, noteLecture } from '../composants.js';

export function vueBudget(d: Charge, recharger: () => void): HTMLElement {
  const budgets = d.budgets;
  let actif = budgets.find((b) => b.scenario === 'sc3')?.scenario ?? budgets[0]?.scenario ?? '';
  const conteneur = h('div', {});
  const retour = h('div', { classe: 'barre-actions' });

  const rendre = (): void => {
    const b = budgets.find((x) => x.scenario === actif);
    vider(conteneur);
    if (!b) return;

    const annees = b.exercices.map((x) => x.exercice);

    /** Une ligne de poste, avec une cellule éditable par exercice. */
    const ligne = (code: string, libelle: string): HTMLElement => {
      const cellules = b.exercices.map((ex) => {
        const l = [...ex.produits, ...ex.charges].find((x) => x.poste.code === code);
        if (!l) return h('td', {}, '—');

        const champ = h('input', {
          classe: 'champ-montant',
          type: 'text',
          inputmode: 'decimal',
          value: nombre(l.montant, 0),
          'aria-label': `${libelle}, exercice ${ex.exercice}`,
          surChange: (e) => {
            const brut = (e.target as HTMLInputElement).value.replace(/[^\d,.-]/g, '').replace(',', '.');
            const montant = Number(brut);
            if (!Number.isFinite(montant)) {
              (e.target as HTMLInputElement).value = nombre(l.montant, 0);
              return;
            }
            void arbitrerLigne({ poste: code, exercice: ex.exercice, scenario: actif, montant })
              .then(() => {
                vider(retour);
                retour.appendChild(
                  h('span', { classe: 'titre' }, `${libelle} ${ex.exercice} arbitré à ${euros(montant)}`),
                );
                recharger();
              })
              .catch(() => {
                vider(retour);
                retour.appendChild(h('span', { classe: 'titre' }, 'Arbitrage refusé.'));
              });
          },
        });

        return h(
          'td',
          { classe: l.origine === 'engendre' ? 'engendre' : '' },
          champ,
          l.origine !== 'engendre' ? h('span', { classe: 'marque-saisi', title: 'montant arbitré' }, '·') : null,
        );
      });
      return h('tr', {}, h('th', { scope: 'row' }, libelle), ...cellules);
    };

    /** Une ligne de sous-total de catégorie. */
    const sousTotal = (categorie: string): HTMLElement => {
      const t = b.totauxParCategorie.find((x) => x.categorie === categorie);
      return h(
        'tr',
        { classe: 'categorie' },
        h('th', { scope: 'row' }, categorie),
        ...(t?.parExercice ?? annees.map(() => 0)).map((v) => h('td', {}, euros(v))),
      );
    };

    const categoriesDe = (nature: 'produit' | 'charge'): string[] => [
      ...new Set(
        b.exercices[0]?.[nature === 'produit' ? 'produits' : 'charges'].map((l) => l.poste.categorie) ?? [],
      ),
    ];

    const corps: HTMLElement[] = [];
    for (const nature of ['produit', 'charge'] as const) {
      for (const cat of categoriesDe(nature)) {
        corps.push(sousTotal(cat));
        const postes =
          b.exercices[0]?.[nature === 'produit' ? 'produits' : 'charges'].filter(
            (l) => l.poste.categorie === cat,
          ) ?? [];
        for (const p of postes) corps.push(ligne(p.poste.code, p.poste.libelle));
      }
      corps.push(
        h(
          'tr',
          { classe: 'total' },
          h('th', { scope: 'row' }, nature === 'produit' ? 'Total des produits' : 'Total des charges'),
          ...b.exercices.map((ex) =>
            h('td', {}, euros(nature === 'produit' ? ex.totalProduits : ex.totalCharges)),
          ),
        ),
      );
    }
    corps.push(
      h(
        'tr',
        { classe: 'resultat' },
        h('th', { scope: 'row' }, 'Résultat de l’exercice'),
        ...b.exercices.map((ex) => h('td', {}, eurosSigne(ex.resultat))),
      ),
    );

    conteneur.appendChild(
      h(
        'div',
        { classe: 'enveloppe-table' },
        h(
          'table',
          { classe: 'table-budget' },
          h('caption', { classe: 'invisible' }, `Budget pluriannuel — ${b.libelle}`),
          h(
            'thead',
            {},
            h('tr', {}, h('th', { scope: 'col' }, 'Poste'), ...annees.map((a) => h('th', { scope: 'col' }, String(a)))),
          ),
          h('tbody', {}, ...corps),
        ),
      ),
    );

    // ── Inducteurs de l'exercice ────────────────────────────────────────
    conteneur.appendChild(
      h(
        'table',
        { classe: 'comp-table' },
        h('caption', { classe: 'invisible' }, 'Inducteurs par exercice'),
        h(
          'thead',
          {},
          h(
            'tr',
            {},
            h('th', { scope: 'col' }, 'Inducteur'),
            ...annees.map((a) => h('th', { scope: 'col' }, String(a))),
          ),
        ),
        h(
          'tbody',
          {},
          h(
            'tr',
            {},
            h('th', { scope: 'row' }, 'Adhérents payeurs'),
            ...b.exercices.map((ex) => h('td', {}, entier(ex.inducteurs.adherents))),
          ),
          h(
            'tr',
            {},
            h('th', { scope: 'row' }, 'Produit moyen par adhérent'),
            ...b.exercices.map((ex) => h('td', {}, euros(ex.inducteurs.produitMoyen))),
          ),
          h(
            'tr',
            {},
            h('th', { scope: 'row' }, 'Salaires bruts'),
            ...b.exercices.map((ex) => h('td', {}, euros(ex.inducteurs.salairesBruts))),
          ),
          h(
            'tr',
            {},
            h('th', { scope: 'row' }, 'Charges patronales'),
            ...b.exercices.map((ex) => h('td', {}, euros(ex.inducteurs.chargesPatronales))),
          ),
          h(
            'tr',
            {},
            h('th', { scope: 'row' }, 'Progression des charges indexées'),
            ...b.exercices.map((ex) => h('td', {}, pourcent(ex.inducteurs.inflation, 1))),
          ),
        ),
      ),
    );
  };

  const onglets = h(
    'div',
    { classe: 'tabs', role: 'tablist', 'aria-label': 'Scénarios budgétaires' },
    ...budgets.map((b) =>
      h(
        'button',
        {
          classe: 'tab',
          type: 'button',
          role: 'tab',
          'aria-selected': b.scenario === actif ? 'true' : 'false',
          surClick: (e) => {
            actif = b.scenario;
            for (const x of onglets.querySelectorAll('[role="tab"]')) {
              x.setAttribute('aria-selected', x === e.currentTarget ? 'true' : 'false');
            }
            rendre();
          },
        },
        b.libelle,
      ),
    ),
  );

  // ── Écarts budget / réalisé de l'exercice en cours ─────────────────────
  const ecarts = d.ecartsPostes.filter((x) => x.budgete > 0 || x.realise > 0);
  const tableEcarts = h(
    'div',
    { classe: 'enveloppe-table' },
    h(
      'table',
      { classe: 'table-budget' },
      h('caption', { classe: 'invisible' }, `Budget confronté au réalisé ${d.meta.exerciceCourant}`),
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { scope: 'col' }, 'Poste'),
          h('th', { scope: 'col' }, 'Budgété'),
          h('th', { scope: 'col' }, 'Réalisé'),
          h('th', { scope: 'col' }, 'Taux'),
          h('th', { scope: 'col' }, 'Reste'),
          h('th', { scope: 'col' }, 'Projeté'),
          h('th', { scope: 'col' }, 'Écart'),
        ),
      ),
      h(
        'tbody',
        {},
        ...(['produit', 'charge'] as const).flatMap((nature) => {
          const lignes = ecarts.filter((x) => x.nature === nature);
          if (lignes.length === 0) return [];
          return [
            h(
              'tr',
              { classe: 'categorie' },
              h('th', { scope: 'row' }, nature === 'produit' ? 'Produits' : 'Charges'),
              h('td', { colspan: '6' }, ''),
            ),
            ...lignes.map((x) =>
              h(
                'tr',
                {},
                h('th', { scope: 'row' }, x.libelle),
                h('td', {}, euros(x.budgete)),
                h('td', {}, euros(x.realise)),
                h('td', {}, x.tauxRealisation === null ? '—' : pourcent(x.tauxRealisation, 0)),
                h('td', {}, euros(x.resteAReconnaitre)),
                h('td', {}, euros(x.projete)),
                h(
                  'td',
                  {},
                  x.ecart === 0
                    ? badge('favorable', '=')
                    : badge(
                        (nature === 'produit' ? x.ecart > 0 : x.ecart < 0) ? 'favorable' : 'alerte',
                        eurosSigne(x.ecart),
                      ),
                ),
              ),
            ),
          ];
        }),
      ),
    ),
  );

  rendre();

  return h(
    'div',
    { classe: 'page', id: 'page-budget', role: 'region', 'aria-label': 'Construction budgétaire' },
    h(
      'div',
      { classe: 'entete-page' },
      h(
        'div',
        {},
        h('h1', {}, 'Construction budgétaire'),
        h(
          'p',
          { classe: 'chapeau' },
          'Une grille exercices × postes. Un montant laissé tel quel suit son inducteur ; un montant ' +
            'saisi est arbitré et marqué d’un point rose. Chaque arbitrage est journalisé.',
        ),
      ),
    ),

    h(
      'section',
      {},
      enteteSection(1, 'Grille pluriannuelle', 'Sélectionner un scénario pour en afficher le détail'),
      onglets,
      conteneur,
      retour,
      h(
        'div',
        { classe: 'barre-actions' },
        h(
          'button',
          {
            classe: 'btn-reset',
            type: 'button',
            surClick: () => {
              void reinitialiserLigne(actif)
                .then(() => {
                  vider(retour);
                  retour.appendChild(h('span', { classe: 'titre' }, 'Arbitrages effacés, les inducteurs reprennent la main.'));
                  recharger();
                })
                .catch(() => {
                  vider(retour);
                  retour.appendChild(h('span', { classe: 'titre' }, 'Réinitialisation refusée.'));
                });
            },
          },
          'Effacer les arbitrages de ce scénario',
        ),
      ),
      insight(
        'info',
        'ℹ',
        '**Quatre inducteurs engendrent l’essentiel du budget** : l’effectif d’adhérents payeurs et le ' +
          'produit moyen font les cotisations, la grille de salaires individuels fait la masse ' +
          'salariale, l’indice de progression fait les autres charges. Quand le résultat dérive, la ' +
          'question devient « quel inducteur a bougé ».',
      ),
    ),

    h('hr', { classe: 'divider' }),
    h(
      'section',
      {},
      enteteSection(
        2,
        `Budget confronté au réalisé ${d.meta.exerciceCourant}`,
        'Quatre colonnes plutôt que deux : un écart ne vaut que s’il alimente une prévision révisée',
      ),
      tableEcarts,
      noteLecture(
        'La projection d’un poste retient le montant budgété, sauf si le réalisé le dépasse déjà : ' +
          'un poste dépassé ne revient pas sous son budget.',
      ),
      barreExport('projection', 'Projection de clôture'),
    ),
  );
}
